/**
 * Typed hooks for the Copilot framework catalog. Shipped frameworks are
 * Canopy-owned starters visible to every tenant; tenant-owned frameworks
 * (future) are isolated. Used by the "New template" / "New dashboard"
 * pickers to seed a competent v1 and pre-fill the AI designer prompt.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type FrameworkModuleType = 'report' | 'dashboard' | 'either';

export interface FrameworkSummary {
  id: string;
  tenantId: string | null;
  name: string;
  moduleType: FrameworkModuleType;
  currentVersion: number | null;
  guidance: Record<string, unknown>;
  outline: string[];
  skeletonKind: 'html-handlebars' | 'widgets';
}

export interface HtmlFrameworkSkeleton {
  kind: 'html-handlebars';
  starterPrompt: string;
  html: string;
  xlsxSpec?: unknown;
  refreshIntervalSec?: number;
  bindings?: Array<{ key: string; hint: string }>;
  outline?: string[];
}

export interface WidgetFrameworkSkeleton {
  kind: 'widgets';
  starterPrompt: string;
  grid: { columns: number; rowHeight: number; gap: number };
  widgets: Array<{
    id: string;
    type: 'kpi' | 'bar' | 'line' | 'area' | 'pie' | 'donut' | 'table' | 'text';
    title: string;
    position: { x: number; y: number; w: number; h: number };
    binding?: { datasetKey: string; limit?: number };
    // Per-type config — loose shape here; authoritative on the server.
    config: Record<string, unknown>;
  }>;
  filters: Array<{
    id: string;
    kind: 'dateRange' | 'enum' | 'text' | 'number';
    label: string;
    field: string;
    defaultValue?: unknown;
    options?: Array<{ value: string; label: string }>;
    multi?: boolean;
  }>;
  outline?: string[];
  refreshIntervalSec?: number;
}

export type FrameworkSkeleton = HtmlFrameworkSkeleton | WidgetFrameworkSkeleton;

export interface FrameworkDetail extends FrameworkSummary {
  skeleton: FrameworkSkeleton;
  publishedBy: string | null;
  publishedAt: string;
}

export type FrameworkSkeletonKind = 'html-handlebars' | 'widgets';

export function useFrameworks(moduleType?: FrameworkModuleType) {
  const api = useApi();
  const qs = moduleType ? `?moduleType=${moduleType}` : '';
  return useQuery<FrameworkSummary[]>({
    queryKey: ['frameworks', moduleType ?? 'all'],
    queryFn: () => api.fetch(`/frameworks${qs}`),
    staleTime: 5 * 60 * 1000, // shipped library rarely changes
  });
}

export function useFramework(id: string | null) {
  const api = useApi();
  return useQuery<FrameworkDetail>({
    queryKey: ['frameworks', id],
    queryFn: () => api.fetch(`/frameworks/${id}`),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveAsFramework() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      moduleType: FrameworkModuleType;
      skeleton: FrameworkSkeleton;
    }) =>
      api.fetch<FrameworkDetail>('/frameworks', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['frameworks'] });
    },
  });
}

/** Imperative fetch — used by pickers that load a single framework on
 *  click rather than bulk-fetching every skeleton up front. */
export async function fetchFrameworkSkeleton(
  apiFetch: <T>(path: string, init?: RequestInit) => Promise<T>,
  id: string,
): Promise<FrameworkDetail> {
  return apiFetch<FrameworkDetail>(`/frameworks/${id}`);
}
